import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { PROVINCES, TRADE_CATEGORIES } from "@/lib/api";
import TradespersonCard from "@/components/TradespersonCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

export default function TradespeopleDirectoryPage() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const filters = useMemo(
    () => ({
      province: params.get("province") || "",
      city: params.get("city") || "",
      category: params.get("category") || "",
      min_rating: parseFloat(params.get("min_rating") || "0"),
      claim_status: params.get("claim_status") || "",
      sort: params.get("sort") || "top_rated",
    }),
    [params],
  );

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (filters.province) q.province = filters.province;
    if (filters.city) q.city = filters.city;
    if (filters.category) q.category = filters.category;
    if (filters.min_rating) q.min_rating = filters.min_rating;
    if (filters.claim_status) q.claim_status = filters.claim_status;
    q.sort = filters.sort;
    api
      .get("/tradespeople", { params: q })
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  }, [filters.province, filters.city, filters.category, filters.min_rating, filters.claim_status, filters.sort]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  const clear = () => setParams({});

  const seoTitle = filters.category && filters.city
    ? `${filters.category} in ${filters.city} | Fixus`
    : filters.city ? `Tradespeople in ${filters.city} | Fixus`
    : filters.category ? `${filters.category} across Canada | Fixus`
    : "Find a Pro — Canadian Tradespeople Directory | Fixus";
  const seoDesc = `Browse ${list.length}+ verified Canadian tradespeople${filters.city ? ` in ${filters.city}` : ""}${filters.category ? ` for ${filters.category}` : ""}. Real ratings, real reviews, free quotes.`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
      </Helmet>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
            Find a Pro
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">
            Browse Canadian tradespeople
          </h1>
          <p className="text-slate-600 mt-2">{list.length} pros available</p>
        </div>
        <Select value={filters.sort} onValueChange={(v) => update("sort", v)}>
          <SelectTrigger data-testid="sort-select" className="w-[200px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="top_rated">Top Rated</SelectItem>
            <SelectItem value="most_reviews">Most Reviews</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Province</Label>
            <Select value={filters.province || "all"} onValueChange={(v) => update("province", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-province" className="mt-2">
                <SelectValue placeholder="All provinces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">City</Label>
            <Input
              data-testid="filter-city"
              value={filters.city}
              onChange={(e) => update("city", e.target.value)}
              placeholder="e.g. Calgary"
              className="mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Trade</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => update("category", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-category" className="mt-2">
                <SelectValue placeholder="All trades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All trades</SelectItem>
                {TRADE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Minimum rating: {filters.min_rating || 0}
            </Label>
            <Slider
              data-testid="filter-rating"
              value={[filters.min_rating]}
              min={0}
              max={5}
              step={0.5}
              onValueChange={(v) => update("min_rating", v[0] || "")}
              className="mt-3"
            />
          </div>
          <Button data-testid="filter-clear" variant="outline" onClick={clear} className="w-full">
            Clear filters
          </Button>
          <div className="pt-4 border-t border-slate-200">
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="filter-claimed-only"
                checked={filters.claim_status === "claimed"}
                onChange={(e) => update("claim_status", e.target.checked ? "claimed" : "")}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-900 focus:ring-blue-900"
              />
              <span>
                <span className="font-medium">Verified pros only</span>
                <span className="block text-xs text-slate-500 mt-0.5">Hide unclaimed listings.</span>
              </span>
            </label>
          </div>
        </aside>

        <div>
          {renderTradespeopleList({ loading, list })}
        </div>
      </div>
    </div>
  );
}

function renderTradespeopleList({ loading, list }) {
  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (list.length === 0) {
    return (
      <div className="text-slate-500" data-testid="empty-tradespeople">
        No tradespeople match these filters yet.
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 tf-stagger">
      {list.map((tp) => (
        <TradespersonCard key={tp.id} tp={tp} />
      ))}
    </div>
  );
}
